import { FormattedEvent, getEventsById } from '@eventbrite/event-renderer';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cloneDeep, isEmpty } from 'lodash';
import { EVENT_TYPE } from '../../../../constants';
import { DEFAULT_EXPANSIONS } from '../../../../redux/api/expansions';
import {
    Bucket,
    BucketEntityType,
    PaginatedBucketData,
} from '../../../../types';
import { waitForCurrentMutations } from '../../../../utils/react-query';

/**
 * Hook which given a list of event IDs, runs
 * a call to search service to get the expanded versions
 *
 * @param {object} props
 */
export interface UseBrowseEventExpansionsProps {
    content: FormattedEvent[];
    placeId?: string;
    isOnline?: boolean;
    bucketKey: string;
    inWindow: boolean;
    isContentExpanded: boolean;
    type: BucketEntityType;
}

export const useBrowseEventExpansions = ({
    placeId,
    isOnline,
    content,
    bucketKey,
    isContentExpanded,
    inWindow,
    type,
}: UseBrowseEventExpansionsProps) => {
    const queryClient = useQueryClient();
    const queryKey = ['browse-buckets', placeId, isOnline];

    // Promoted events already have all data necessary
    const bucketOrganicEventIds =
        type === EVENT_TYPE
            ? content
                  .filter(({ isPromoted }) => !isPromoted)
                  .map(({ id }) => id)
            : [];

    const isValidBucket = !isEmpty(bucketOrganicEventIds) && !!bucketKey;

    return useQuery(
        ['expandEvents', bucketKey],
        async () => {
            const response = await getEventsById(
                bucketOrganicEventIds,
                50,
                DEFAULT_EXPANSIONS,
            );

            await waitForCurrentMutations(queryKey, queryClient);

            queryClient.setQueryData(queryKey, (oldData: any) => {
                //get page that this bucket belongs to
                const pageIndex = oldData.pages.findIndex(
                    (page: PaginatedBucketData) =>
                        page.buckets.some(
                            (bucket: Bucket) => bucket.key === bucketKey,
                        ),
                );

                //get index of current bucket within page's context
                const bucketIndex = oldData.pages[pageIndex].buckets.findIndex(
                    (bucket: Bucket) => bucket.key === bucketKey,
                );

                const currentBucket =
                    oldData.pages[pageIndex].buckets[bucketIndex];

                const newPageData = cloneDeep(oldData.pages);

                newPageData[pageIndex].buckets[bucketIndex] = {
                    ...currentBucket,
                    isContentExpanded: true,
                    content: currentBucket.content.map(
                        (event: FormattedEvent) => {
                            const extendedEvent = response.events.find(
                                (expandedEvent: FormattedEvent) =>
                                    expandedEvent.id === event.id &&
                                    // Ensure that the expanded event is organic, to ensure that organic data does not override promoted data
                                    !event.isPromoted,
                            );

                            return {
                                ...event,
                                ...extendedEvent,
                            };
                        },
                    ),
                };

                return {
                    pageParams: oldData.pageParams,
                    pages: newPageData,
                };
            });

            return true;
        },
        {
            onError: (error) => {
                console.log(error);
            },
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
            staleTime: Infinity,
            enabled: isValidBucket && inWindow && !isContentExpanded,
        },
    );
};
